import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../merchants/components/CreateMerchant.css';
import './ApiDocumentation.css';
import {
  BsClipboard, BsClipboardCheck, BsPlayFill, BsShieldLockFill,
  BsEye, BsEyeSlash, BsExclamationCircle, BsCheckCircleFill,
} from 'react-icons/bs';

const BASE_URL = 'https://api.moneyeu.com/moneyEu/api';
const SERVICE_NAME = 'moneyEuPayment';

export const NAV_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'credentials', label: 'Credentials' },
  { id: 'headers', label: 'Required Headers' },
  { id: 'signature', label: 'Building the Signature' },
  { id: 'try-it', label: 'Try It' },
  { id: 'code-examples', label: 'Code Examples' },
  { id: 'process-payment', label: 'POST /v1/processPayment' },
  { id: 'status-webhook', label: 'Status Webhook' },
  { id: 'checkout', label: 'Hosted Checkout' },
  { id: 'iframe', label: 'Iframe Embed' },
  { id: 'transaction-status', label: 'GET Transaction Status' },
  { id: 'test-cards', label: 'Test Cards' },
  { id: 'errors', label: 'Error Codes' },
  { id: 'mistakes', label: 'Common Mistakes' },
  { id: 'notes', label: 'Notes for Integrators' },
] as const;

export type NavSectionId = (typeof NAV_SECTIONS)[number]['id'];

type ApiDocumentationProps = {
  hideInPageNav?: boolean;
  onActiveSectionChange?: (sectionId: NavSectionId) => void;
};

const PYTHON_SAMPLE = `import base64
import hashlib
import hmac
import json
import random
import string
import time

SERVICE_NAME = "moneyEuPayment"
SALT_CHARS = string.ascii_letters + string.digits

def generate_salt(length: int = 32) -> str:
    return "".join(random.choice(SALT_CHARS) for _ in range(length))

def build_signature(service_name, salt, api_key, timestamp, body_str, secret_key) -> str:
    message = f"{service_name}{salt}{api_key}{timestamp}{body_str}"
    digest = hmac.new(secret_key.encode("utf-8"), message.encode("utf-8"), hashlib.sha256).digest()
    return base64.b64encode(digest).decode("ascii")

body_str = json.dumps(payload, separators=(",", ":"))  # compact JSON, no whitespace
salt = generate_salt()
timestamp = str(int(time.time()))
signature = build_signature(SERVICE_NAME, salt, api_key, timestamp, body_str, secret_key)`;

const JAVA_SAMPLE = `import javax.crypto.Mac;
import javax.crypto.spec.SecretKeySpec;
import java.nio.charset.StandardCharsets;
import java.time.Instant;
import java.util.Base64;

String serviceName = "moneyEuPayment";
String salt = "up to 32 characters";
String apiKey = "...";
String secretKey = "...";
String body = "compact JSON request body, or \\"\\" if none";

long timestamp = Instant.now().getEpochSecond();
String message = serviceName + salt + apiKey + timestamp + body;

SecretKeySpec key = new SecretKeySpec(secretKey.getBytes(StandardCharsets.UTF_8), "HmacSHA256");
Mac mac = Mac.getInstance("HmacSHA256");
mac.init(key);
byte[] bytes = mac.doFinal(message.getBytes(StandardCharsets.UTF_8));
String signature = Base64.getEncoder().encodeToString(bytes);`;

const NODE_SAMPLE = `const crypto = require('crypto');

const serviceName = 'moneyEuPayment';
const timestamp = String(Math.floor(Date.now() / 1000));
const bodyStr = JSON.stringify(payload); // compact JSON, no whitespace

const message = \`\${serviceName}\${salt}\${apiKey}\${timestamp}\${bodyStr}\`;
const signature = crypto.createHmac('sha256', secretKey).update(message, 'utf8').digest('base64');`;

const CURL_SAMPLE = `curl --location '${BASE_URL}/v1/processPayment' \\
  --header 'apiKey: <your apiKey>' \\
  --header 'salt: <32-char random string>' \\
  --header 'timestamp: <unix epoch seconds>' \\
  --header 'signature: <computed signature>' \\
  --header 'Content-Type: application/json' \\
  --data '<compact JSON body must match what was signed>'`;

const CODE_SAMPLES: Record<string, string> = {
  Python: PYTHON_SAMPLE,
  Java: JAVA_SAMPLE,
  'Node.js': NODE_SAMPLE,
  cURL: CURL_SAMPLE,
};

const PROCESS_PAYMENT_REQUEST = `{
  "customerName": "John Doe",
  "address": "80 Queens Road",
  "zip": "M63X 7TH",
  "callbackUrl": "https://your-domain.com/webhook",
  "redirectUrl": "https://your-domain.com/redirect",
  "city": "Manchester",
  "state": "CA",
  "countryName": "United States",
  "customerEmail": "test@test.com",
  "phone": "07833473156",
  "amount": 10.00,
  "currency": "EUR",
  "cardNumber": "4543059999999982",
  "cardholderName": "J Bloggs",
  "expiryDate": "06/27",
  "cvv": "110",
  "storeFrontUrl": "https://yourdomain.com/checkout",
  "customerIpAddress": "203.0.113.42",
  "customerUserAgent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/126.0.0.0 Safari/537.36"
}`;

const PROCESS_PAYMENT_SUCCESS = `{
  "success": true,
  "message": "Payment processed successfully",
  "data": {
    "orderId": "ORD-c7cee653-0252-4828-9272-59abace92131",
    "transactionId": "TXN-5d0053a1-7979-4995-a69f-de4376de15b6",
    "transactionStatus": "PENDING",
    "hpp": false,
    "redirectUrl": "https://api.moneyeu.com/moneyEu/api/v1/payments/redirect/status/TXN-5d0053a1-7979-4995-a69f-de4376de15b6"
  },
  "timestamp": "2026-06-19T15:49:17.0632387"
}`;

const STATUS_WEBHOOK_SUCCESS = `{
  "transaction_id": "TXN-548dfcb0-xxxx-xxxx-933b-80c4b5191744",
  "order_id": "ORD-5cf0e11e-fe28-xxxx-xxxx-2beb38591ecf",
  "status": "Transaction Successful",
  "paid_amount": 225,
  "currency": "USD",
  "response_message": "success"
}`;

const STATUS_WEBHOOK_DECLINED = `{
  "transaction_id": "TXN-ba950cb2-xxxx-xxxx-aa50-024424e83e11",
  "order_id": "ORD-6340f0a4-xxxx-xxxx-a442-91d856c826f1",
  "status": "Transaction Declined",
  "paid_amount": 475,
  "currency": "USD",
  "response_message": "Invalid card details"
}`;

const CHECKOUT_REQUEST = `{
  "customerName": "John Doe",
  "address": "80 Queens Road",
  "zip": "M63X 7TH",
  "callbackUrl": "https://your-domain.com/webhook",
  "redirectUrl": "https://your-domain.com/redirect",
  "city": "Manchester",
  "state": "CA",
  "countryName": "United States",
  "customerEmail": "test@test.com",
  "phone": "07833473156",
  "amount": 10.00,
  "currency": "EUR",
  "storeFrontUrl": "https://yourdomain.com/checkout",
  "customerIpAddress": "203.0.113.42",
  "customerUserAgent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/126.0.0.0 Safari/537.36"
}`;

const CHECKOUT_SUCCESS = `{
  "success": true,
  "message": "Order created successfully",
  "data": {
    "orderId": "ORD-3f6b1a2e-9c1d-4e3a-8b2b-7a5d1e9f4c30",
    "checkoutToken": "a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6",
    "checkoutUrl": "https://login.moneyeu.com/pay/a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6"
  }
}`;

const PAY_CHECKOUT_REQUEST = `{
  "cardNumber": "4111111111111111",
  "cardholderName": "John Doe",
  "expiryDate": "12/28",
  "cvv": "123"
}`;

const EMBED_SCRIPT_HTML = `<div id="moneyeu-checkout"></div>

<script src="https://login.moneyeu.com/embed.js"></script>
<script>
  MoneyEU.mount("#moneyeu-checkout", {
    // data.checkoutUrl from your processPayment response
    checkoutUrl: "https://login.moneyeu.com/pay/{checkoutToken}",

    onResult: function (result) {
      // Final outcome: result.outcome is "success" or "error".
      // UI signal only — confirm server-side before fulfilling the order.
      console.log(result.outcome, result.status, result.transactionId);
    },
    onPending: function (info) {
      // Submitted; the final result is still being confirmed.
    },
    onError: function (message) {
      // The checkout could not be displayed.
    }
  });
</script>`;

const IFRAME_EMBED_HTML = `<iframe
  id="moneyeu-checkout"
  src="[PLEASE ENTER YOU MONEYEU REDIRECTION URL]?parentOrigin=https://your-domain.com"
  title="Secure payment"
  width="100%"
  height="720"
  allow="payment; camera; publickey-credentials-get"
  style="border: 0;"
></iframe>`;

const IFRAME_LISTEN_JS = `const MONEYEU_ORIGIN = "https://login.moneyeu.com";

window.addEventListener("message", (event) => {
  if (event.origin !== MONEYEU_ORIGIN) return;
  if (!event.data || event.data.type !== "moneyeu.checkout.result") return;

  const { outcome, status, transactionId, amount, currency, message, reason } = event.data;

  if (outcome === "success") {
    // Hide the iframe and show your confirmation UI.
    // Confirm the result server-side via your callbackUrl status webhook
    // or GET /v1/payments/redirect/status/{transactionId}.
  } else if (outcome === "error") {
    // Show a failure state. Do not treat this as the source of truth —
    // confirm with your webhook or the transaction status endpoint.
  }
});`;

const IFRAME_MESSAGE_SAMPLE = `{
  "type": "moneyeu.checkout.result",
  "status": "COMPLETED",
  "outcome": "success",
  "transactionId": "TXN-5d0053a1-7979-4995-a69f-de4376de15b6",
  "amount": 10.00,
  "currency": "EUR",
  "message": "Payment Successful",
  "reason": null,
  "acquirerReference": null
}`;

const TRANSACTION_STATUS_CURL = `curl --location '${BASE_URL}/v1/payments/redirect/status/TXN-83d216a3-a1b6-41bd-b6f7-e467fc604fa5'`;

const TRANSACTION_STATUS_RESPONSE = `{
  "transactionId": "TXN-83d216a3-a1b6-41bd-b6f7-e467fc604fa5",
  "status": "COMPLETED",
  "amount": 19.90,
  "currency": "USD",
  "message": "Payment Successful",
  "reason": "Sandbox test transaction (no acquirer assigned)",
  "merchantRedirectUrl": "https://your-domain.com/return?payResult=success&gateway=moneyeu&total_amount=19.90&currency=USD"
}`;

const ERROR_SAMPLE = `{
  "success": false,
  "message": "Invalid request signature.",
  "errorCode": "INVALID_SIGNATURE",
  "status": 401,
  "path": "/moneyEu/api/v1/processPayment",
  "timestamp": "2026-07-09T21:22:51.563776"
}`;

function generateSalt(length = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);
  return Array.from(values, v => chars[v % chars.length]).join('');
}

function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
}

async function computeSignature(message: string, secretKey: string): Promise<string> {
  if (!window.crypto?.subtle) {
    throw new Error('Web Crypto API is unavailable in this browser context.');
  }
  const encoder = new TextEncoder();
  const key = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(secretKey),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signatureBuffer = await window.crypto.subtle.sign('HMAC', key, encoder.encode(message));
  return bufferToBase64(signatureBuffer);
}

function buildCurl(apiKey: string, salt: string, timestamp: string, signature: string, compactBody: string): string {
  return `curl --location '${BASE_URL}/v1/processPayment' \\
  --header 'apiKey: ${apiKey}' \\
  --header 'salt: ${salt}' \\
  --header 'timestamp: ${timestamp}' \\
  --header 'signature: ${signature}' \\
  --header 'Content-Type: application/json' \\
  --data '${compactBody}'`;
}

const CopyButton: React.FC<{ value: string; label?: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard permission denied or unavailable.
    }
  };
  return (
    <button type="button" className="apidoc-copy-btn" onClick={handleCopy} aria-label={label || 'Copy'}>
      {copied ? <BsClipboardCheck className="apidoc-copied-icon" /> : <BsClipboard />}
    </button>
  );
};

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language }) => (
  <div className="apidoc-code-block">
    <div className="apidoc-code-head">
      <span className="apidoc-code-lang">{language || 'Code'}</span>
      <CopyButton value={code} label="Copy code" />
    </div>
    <pre>
      <code>{code}</code>
    </pre>
  </div>
);

const FraudComplianceFieldsNote: React.FC = () => (
  <>
    <div className="apidoc-warning" role="note">
      <BsExclamationCircle className="apidoc-warning-icon" aria-hidden="true" />
      <div>
        <strong>Important:</strong> These three fields are technically optional omitting them
        won&apos;t cause the API to reject your request. But for <strong>live transactions</strong>,
        they are required by our acquiring bank&apos;s fraud/compliance rules. Sending placeholder,
        generic, or missing values on live traffic risks the transaction being flagged or your
        account being restricted. They&apos;re only safe to omit while testing in sandbox.
      </div>
    </div>
    <div className="apidoc-table-wrap apidoc-table-wrap-spaced">
      <table className="apidoc-table">
        <thead>
          <tr><th>Field</th><th>Required</th><th>Notes</th></tr>
        </thead>
        <tbody>
          <tr><td><code>storeFrontUrl</code></td><td>-</td><td>Fixed URL of your real storefront or checkout page (e.g. <code>https://yourdomain.com/checkout</code>). Hardcode your own domain it does not change per request.</td></tr>
          <tr><td><code>customerIpAddress</code></td><td>-</td><td>The customer&apos;s real IP address as seen by your server when their browser hits your checkout page. If behind a load balancer or CDN, use the <code>X-Forwarded-For</code> header.</td></tr>
          <tr><td><code>customerUserAgent</code></td><td>-</td><td>The exact <code>User-Agent</code> string from the customer&apos;s browser on that same request not from internal server-to-server calls.</td></tr>
        </tbody>
      </table>
    </div>
  </>
);

const LanguageCodeSamples: React.FC = () => {
  const languages = Object.keys(CODE_SAMPLES);
  const [lang, setLang] = useState(languages[0]);
  return (
    <div className="apidoc-lang-block">
      <div className="apidoc-lang-tabs">
        {languages.map(l => (
          <button
            key={l}
            type="button"
            className={`apidoc-lang-tab ${lang === l ? 'active' : ''}`}
            onClick={() => setLang(l)}
          >
            {l}
          </button>
        ))}
      </div>
      <CodeBlock language={lang} code={CODE_SAMPLES[lang]} />
    </div>
  );
};

const SignatureTryIt: React.FC = () => {
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [body, setBody] = useState(PROCESS_PAYMENT_REQUEST);
  const [result, setResult] = useState<{ salt: string; timestamp: string; signature: string; compactBody: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setError(null);
    setResult(null);

    if (!apiKey.trim()) { setError('Enter your API key.'); return; }
    if (!secretKey.trim()) { setError('Enter your secret key.'); return; }

    let compactBody = '';
    if (body.trim()) {
      try {
        compactBody = JSON.stringify(JSON.parse(body));
      } catch {
        setError('Request body must be valid JSON.');
        return;
      }
    }

    setGenerating(true);
    try {
      const salt = generateSalt();
      const timestamp = String(Math.floor(Date.now() / 1000));
      const message = `${SERVICE_NAME}${salt}${apiKey.trim()}${timestamp}${compactBody}`;
      const signature = await computeSignature(message, secretKey.trim());
      setResult({ salt, timestamp, signature, compactBody });
    } catch {
      setError('Could not generate a signature in this browser. Try a different browser or use the code samples below instead.');
    } finally {
      setGenerating(false);
    }
  };

  const curlCommand = result
    ? buildCurl(apiKey.trim(), result.salt, result.timestamp, result.signature, result.compactBody)
    : '';

  return (
    <div className="apidoc-tryit">
      <p className="apidoc-tryit-note">
        <BsShieldLockFill /> This runs entirely in your browser using the Web Crypto API your
        keys are never sent anywhere, not even to MoneyEU's own servers.
      </p>

      <div className="apidoc-tryit-grid">
        <label className="cm-label">
          <span>API Key</span>
          <input
            className="cm-input dev-mono"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Your apiKey"
          />
        </label>
        <label className="cm-label">
          <span>Secret Key</span>
          <div className="cm-input-icon cm-input-icon-end">
            <input
              className="cm-input has-icon-end dev-mono"
              type={showSecret ? 'text' : 'password'}
              value={secretKey}
              onChange={e => setSecretKey(e.target.value)}
              placeholder="Your secretKey"
            />
            <button
              type="button"
              className="cm-input-toggle"
              onClick={() => setShowSecret(v => !v)}
              aria-label={showSecret ? 'Hide secret key' : 'Show secret key'}
            >
              {showSecret ? <BsEyeSlash /> : <BsEye />}
            </button>
          </div>
        </label>
      </div>

      <label className="cm-label apidoc-tryit-body-label">
        <span>Request Body (JSON)</span>
        <textarea
          className="cm-input cm-textarea apidoc-tryit-textarea dev-mono"
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={10}
        />
      </label>

      {error && (
        <div className="apidoc-tryit-error">
          <BsExclamationCircle /> {error}
        </div>
      )}

      <button type="button" className="cm-btn-primary apidoc-tryit-btn" onClick={handleGenerate} disabled={generating}>
        <BsPlayFill /> {generating ? 'Generating…' : 'Generate Signed Request'}
      </button>

      {result && (
        <div className="apidoc-tryit-result">
          <div className="apidoc-tryit-success">
            <BsCheckCircleFill /> Signature generated
          </div>
          <div className="apidoc-tryit-values">
            <div className="apidoc-tryit-value">
              <span>salt</span>
              <code>{result.salt}</code>
            </div>
            <div className="apidoc-tryit-value">
              <span>timestamp</span>
              <code>{result.timestamp}</code>
            </div>
            <div className="apidoc-tryit-value apidoc-tryit-value-full">
              <span>signature</span>
              <code>{result.signature}</code>
            </div>
          </div>
          <p className="apidoc-p apidoc-hint">Ready-to-run cURL valid for 5 minutes from the timestamp above:</p>
          <CodeBlock language="cURL" code={curlCommand} />
        </div>
      )}
    </div>
  );
};

const ApiDocumentation: React.FC<ApiDocumentationProps> = ({
  hideInPageNav = false,
  onActiveSectionChange,
}) => {
  const [activeSection, setActiveSection] = useState<NavSectionId>(NAV_SECTIONS[0].id);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onActiveSectionChange?.(activeSection);
  }, [activeSection, onActiveSectionChange]);

  const getScrollContainer = useCallback((): HTMLElement | null => {
    const root = rootRef.current;
    if (!root) return null;
    const { overflowY } = window.getComputedStyle(root);
    return overflowY === 'auto' || overflowY === 'scroll' ? root : null;
  }, []);

  const updateActiveSection = useCallback(() => {
    const scrollContainer = getScrollContainer();
    const marker = scrollContainer
      ? scrollContainer.getBoundingClientRect().top + 20
      : 120;

    let current: NavSectionId = NAV_SECTIONS[0].id;

    for (const { id } of NAV_SECTIONS) {
      const el = document.getElementById(id);
      if (!el) continue;

      if (el.getBoundingClientRect().top <= marker) {
        current = id;
      }
    }

    setActiveSection(current);
  }, [getScrollContainer]);

  const scrollToSection = useCallback(
    (sectionId: NavSectionId) => {
      const target = document.getElementById(sectionId);
      if (!target) return;

      const scrollContainer = getScrollContainer();
      if (scrollContainer) {
        const top =
          target.getBoundingClientRect().top -
          scrollContainer.getBoundingClientRect().top +
          scrollContainer.scrollTop -
          12;
        scrollContainer.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      setActiveSection(sectionId);
    },
    [getScrollContainer],
  );

  useEffect(() => {
    const scrollContainer = getScrollContainer();
    const scrollTarget: HTMLElement | Window = scrollContainer ?? window;

    const onScroll = () => updateActiveSection();

    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    const frame = window.requestAnimationFrame(updateActiveSection);

    return () => {
      scrollTarget.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.cancelAnimationFrame(frame);
    };
  }, [getScrollContainer, updateActiveSection]);

  const handleNavClick = (event: React.MouseEvent<HTMLAnchorElement>, sectionId: NavSectionId) => {
    event.preventDefault();
    scrollToSection(sectionId);
  };

  return (
    <div
      className={`apidoc-root${hideInPageNav ? ' apidoc-root--no-nav' : ''}`}
      ref={rootRef}
    >
      <div className="apidoc-layout">
        {!hideInPageNav && (
          <nav className="apidoc-nav" aria-label="On this page">
            <span className="apidoc-nav-title">On this page</span>
            <ul>
              {NAV_SECTIONS.map(section => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className={activeSection === section.id ? 'active' : ''}
                    onClick={(event) => handleNavClick(event, section.id)}
                  >
                    {section.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="apidoc-content">
          <section id="overview" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">Overview</h4>
              <span className="cm-section-hint">Payment Service API v1.0</span>
            </div>
            <p className="apidoc-p">
              This is the integration reference for MoneyEU's Payment Service API the endpoints
              used to submit a payment (direct server-to-server or hosted checkout), embed hosted
              checkout in an iframe, check transaction status, receive status webhooks after a
              successful or declined payment, fetch active payment methods, and the HMAC-SHA256
              request signing every direct/external call must use.
            </p>
            <p className="apidoc-p">
              <strong>Scope note.</strong> Logged-in calls made from this dashboard itself are
              authenticated by your session automatically and skip request signing entirely.
              Everything below applies to <strong>direct API callers</strong> your own
              server-to-server integration, Postman, cURL, etc.
            </p>
            <div className="apidoc-base-url">
              <span className="apidoc-base-url-label">Base URL</span>
              <code>{BASE_URL}</code>
              <CopyButton value={BASE_URL} label="Copy base URL" />
            </div>
          </section>

          <section id="credentials" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">1. Credentials</h4>
            </div>
            <p className="apidoc-p">
              Each merchant has two keys, issued and managed from the <strong>API Credentials</strong>{' '}
              tab on this page:
            </p>
            <div className="apidoc-table-wrap">
              <table className="apidoc-table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Purpose</th>
                    <th>Travels in the request?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>apiKey</code></td>
                    <td>Identifies the merchant</td>
                    <td>Yes <code>apiKey</code> header</td>
                  </tr>
                  <tr>
                    <td><code>secretKey</code></td>
                    <td>Signs the request</td>
                    <td>Never used locally to compute the signature only</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="apidoc-warning">
              Treat <code>secretKey</code> like a password. If it leaks, regenerate it from the API
              Credentials tab immediately this invalidates the old key/secret pair right away.
            </p>
          </section>

          <section id="headers" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">2. Required headers</h4>
            </div>
            <div className="apidoc-table-wrap">
              <table className="apidoc-table">
                <thead>
                  <tr>
                    <th>Header</th>
                    <th>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td><code>apiKey</code></td><td>string</td><td>Merchant's API access key.</td></tr>
                  <tr><td><code>salt</code></td><td>string</td><td>Random string, max 32 characters, unique per request.</td></tr>
                  <tr><td><code>timestamp</code></td><td>string</td><td>Current Unix time in seconds (e.g. <code>1760346987</code>).</td></tr>
                  <tr><td><code>signature</code></td><td>string</td><td>Base64-encoded HMAC-SHA256 signature (see section 3).</td></tr>
                </tbody>
              </table>
            </div>
            <p className="apidoc-p apidoc-hint">
              The merchant is resolved from the <code>apiKey</code> header, and your secret key
              must be configured on the account, otherwise the request fails with{' '}
              <code>MERCHANT_SECRET_NOT_CONFIGURED</code>.
            </p>
          </section>

          <section id="signature" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">3. Building the signature</h4>
            </div>

            <p className="apidoc-step-label">Step 1: construct the message to sign</p>
            <CodeBlock code="message = serviceName + salt + apiKey + timestamp + rawBody" />
            <ul className="apidoc-list">
              <li><code>serviceName</code>: fixed string <code>moneyEuPayment</code>.</li>
              <li><code>salt</code>: the same random string sent in the <code>salt</code> header.</li>
              <li><code>apiKey</code>: the same value sent in the <code>apiKey</code> header.</li>
              <li><code>timestamp</code>: the same value sent in the <code>timestamp</code> header, as a string.</li>
              <li>
                <code>rawBody</code>: the exact JSON request body, compact (no extra whitespace), as
                a string. Empty string if there is no body. This must be byte-for-byte identical to
                what you actually send as the request body sign first, then send that exact string.
              </li>
            </ul>
            <p className="apidoc-p apidoc-hint">Fields are concatenated directly, with no separators between them.</p>

            <p className="apidoc-step-label">Step 2: sign it</p>
            <CodeBlock code="signature = Base64( HMAC_SHA256( key = secretKey, message ) )" />
            <p className="apidoc-p apidoc-hint">
              The algorithm is <code>HmacSHA256</code>. The digest is Base64-encoded (<strong>not hex</strong>).
            </p>

            <p className="apidoc-step-label">Step 3: send the headers</p>
            <p className="apidoc-p apidoc-hint">
              Send <code>apiKey</code>, <code>salt</code>, <code>timestamp</code>, and{' '}
              <code>signature</code> as headers, along with the same <code>rawBody</code> as the
              request body.
            </p>
          </section>

          <section id="try-it" className="cm-card apidoc-section apidoc-tryit-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title"><BsPlayFill /> Try it: generate a real signature</h4>
              <span className="cm-section-hint">Runs locally in your browser</span>
            </div>
            <SignatureTryIt />
          </section>

          <section id="code-examples" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">4. Code examples</h4>
            </div>
            <LanguageCodeSamples />
          </section>

          <section id="process-payment" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">5. POST /v1/processPayment</h4>
              <span className="cm-section-hint">Submit a payment</span>
            </div>
            <p className="apidoc-p">
              This endpoint attempts an immediate payment against a mapped acquirer. For a hosted
              checkout order that returns a checkout link instead, see section 7.
            </p>

            <p className="apidoc-step-label">Request body</p>
            <CodeBlock language="JSON" code={PROCESS_PAYMENT_REQUEST} />
            <FraudComplianceFieldsNote />

            <div className="apidoc-table-wrap">
              <table className="apidoc-table">
                <thead>
                  <tr><th>Field</th><th>Required</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>customerName</code></td><td>Yes</td><td>Customer full name.</td></tr>
                  <tr><td><code>customerEmail</code></td><td>Yes</td><td>Customer email address.</td></tr>
                  <tr><td><code>amount</code></td><td>Yes</td><td>Payment amount plain numeric value, no currency symbols.</td></tr>
                  <tr><td><code>currency</code></td><td>Yes</td><td>3-letter ISO currency code (normalized to upper case).</td></tr>
                  <tr><td><code>phone</code>, <code>address</code>, <code>city</code>, <code>state</code>, <code>zip</code></td><td>Yes</td><td>Customer/billing details.</td></tr>
                  <tr><td><code>countryId</code> / <code>countryName</code></td><td>Yes</td><td>Country reference.</td></tr>
                  <tr><td><code>service</code></td><td>Yes</td><td>Description of the order/service.</td></tr>
                  <tr><td><code>callbackUrl</code></td><td>No</td><td>Your server-to-server webhook/callback URL. After the payment succeeds or is declined, MoneyEU POSTs a <a href="#status-webhook">status webhook</a> here.</td></tr>
                  <tr><td><code>redirectUrl</code></td><td>No</td><td>URL the customer is returned to after payment.</td></tr>
                  <tr><td><code>cardNumber</code>, <code>cardholderName</code>, <code>expiryDate</code>, <code>cvv</code></td><td>Conditional</td><td>Required for direct S2S card flow; masked immediately after use, never stored in plain form or returned in responses.</td></tr>
                </tbody>
              </table>
            </div>

            <p className="apidoc-step-label">Success response: 200 OK</p>
            <CodeBlock language="JSON" code={PROCESS_PAYMENT_SUCCESS} />
            <p className="apidoc-p apidoc-hint">
              If the first acquirer attempt fails, the service automatically retries against the
              next mapped acquirer terminal until one succeeds or all candidates are exhausted. Once
              the payment succeeds or is declined, a{' '}
              <a href="#status-webhook">status webhook</a> is dispatched to your{' '}
              <code>callbackUrl</code> (unless the acquirer manages its own webhook delivery). Use
              the <a href="#transaction-status">transaction status endpoint</a> to poll{' '}
              <code>data.transactionId</code> while <code>transactionStatus</code> is{' '}
              <code>PENDING</code> or <code>AWAITING_3DS</code>.
            </p>
          </section>

          <section id="status-webhook" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">6. Status webhook</h4>
              <span className="cm-section-hint">After success or decline</span>
            </div>
            <p className="apidoc-p">
              When a payment reaches a final success or decline state, MoneyEU POSTs a JSON payload
              to the <code>callbackUrl</code> you supplied on the original request. This applies to
              both direct <code>processPayment</code> and hosted checkout. Treat the webhook as the
              server-to-server source of truth not the customer redirect, iframe{' '}
              <code>postMessage</code>, or a polled status check alone.
            </p>
            <p className="apidoc-p apidoc-hint">
              Your endpoint must be publicly reachable over HTTPS and should respond quickly with a
              2xx status. Delivery is asynchronous. If the acquirer manages its own webhook
              delivery, that payload may replace this one.
            </p>

            <p className="apidoc-step-label">Successful payment</p>
            <CodeBlock language="JSON" code={STATUS_WEBHOOK_SUCCESS} />

            <p className="apidoc-step-label">Declined payment</p>
            <CodeBlock language="JSON" code={STATUS_WEBHOOK_DECLINED} />

            <div className="apidoc-table-wrap apidoc-table-wrap-spaced">
              <table className="apidoc-table">
                <thead>
                  <tr><th>Field</th><th>Type</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>transaction_id</code></td><td>string</td><td>Transaction identifier. Matches <code>data.transactionId</code> from the process-payment response.</td></tr>
                  <tr><td><code>order_id</code></td><td>string</td><td>Order identifier. Matches <code>data.orderId</code> from the process-payment or checkout response.</td></tr>
                  <tr><td><code>status</code></td><td>string</td><td>Human-readable outcome e.g. <code>Transaction Successful</code> or <code>Transaction Declined</code>.</td></tr>
                  <tr><td><code>paid_amount</code></td><td>number</td><td>Amount paid (or attempted) for this transaction.</td></tr>
                  <tr><td><code>currency</code></td><td>string</td><td>3-letter ISO currency code.</td></tr>
                  <tr><td><code>response_message</code></td><td>string</td><td><code>success</code> on an approved payment, or the decline reason (e.g. <code>Invalid card details</code>).</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="checkout" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">7. Hosted checkout flow</h4>
              <span className="cm-section-hint">No card details at this stage</span>
            </div>
            <p className="apidoc-p">
              Same endpoint and headers as section 5. Omit card fields they are collected later
              on the hosted checkout page. After the customer pays (success or decline), the same{' '}
              <a href="#status-webhook">status webhook</a> is posted to your <code>callbackUrl</code>.
            </p>

            <p className="apidoc-step-label">Request body</p>
            <CodeBlock language="JSON" code={CHECKOUT_REQUEST} />
            <FraudComplianceFieldsNote />

            <p className="apidoc-step-label">Success response</p>
            <CodeBlock language="JSON" code={CHECKOUT_SUCCESS} />
            <p className="apidoc-p apidoc-hint">
              Redirect the customer to <code>checkoutUrl</code>, or{' '}
              <a href="#iframe">embed it in an iframe</a> on your site. The link is also emailed
              to the customer, and expires after the configured checkout token TTL (default 30
              minutes).
            </p>

            <p className="apidoc-step-label">Pay Checkout Order: request body</p>
            <p className="apidoc-p apidoc-hint">Submitted from the checkout page when the customer enters card details.</p>
            <CodeBlock language="JSON" code={PAY_CHECKOUT_REQUEST} />
            <p className="apidoc-p apidoc-hint">
              Response mirrors the standard Process Payment success response above. If the order is
              in test mode, a simulated result is returned instead. The{' '}
              <a href="#status-webhook">status webhook</a> still fires once the payment succeeds or
              is declined.
            </p>
          </section>

          <section id="iframe" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">8. Iframe embed</h4>
              <span className="cm-section-hint">Hosted checkout on your page</span>
            </div>
            <p className="apidoc-p">
              You can load <code>data.checkoutUrl</code> from the{' '}
              <a href="#checkout">hosted checkout</a> response inside an iframe instead of sending
              the customer away from your site. When checkout (or the post-3DS status page) runs
              inside a frame, MoneyEU does <strong>not</strong> navigate to your{' '}
              <code>redirectUrl</code>. It posts a <code>window.postMessage</code> event to the
              parent window once the payment reaches a final state.
            </p>

            <p className="apidoc-step-label">Recommended: the embed script</p>
            <p className="apidoc-p apidoc-hint">
              One script tag and one call. It sizes the frame to the checkout as the customer
              moves through it, grants the permissions some payment methods need (including camera
              access for identity-document checks), accepts messages only from MoneyEU and only from
              its own frame, and reports the final result once. Every callback is a UI signal —
              always confirm the outcome with your{' '}
              <a href="#status-webhook">callbackUrl status webhook</a> or the{' '}
              <a href="#transaction-status">transaction status endpoint</a> before fulfilling.
            </p>
            <CodeBlock language="HTML" code={EMBED_SCRIPT_HTML} />

            <p className="apidoc-step-label">Before you go live</p>
            <ul className="apidoc-list">
              <li>
                <strong>Ask MoneyEU to approve your website&apos;s domain for embedding.</strong>{' '}
                Checkout only loads inside pages on approved domains, so an unapproved site shows an
                empty frame.
              </li>
              <li>
                <strong>Confirm the payment methods enabled on your account.</strong> Some require the
                customer to verify their identity during checkout — see what your customers will see
                below.
              </li>
            </ul>

            <div className="apidoc-warning" role="note">
              <BsExclamationCircle className="apidoc-warning-icon" aria-hidden="true" />
              <div>
                For iframe integrations, make sure the URL you submit for whitelisting is the same
                URL you send in <code>storeFrontUrl</code>.
              </div>
            </div>

            <p className="apidoc-step-label">Creating the payment</p>
            <ul className="apidoc-list">
              <li>
                Call <a href="#checkout">processPayment</a> from <strong>your server</strong> with the
                header <code>isCheckout: true</code>. Never call it from the browser — the request is
                signed with your secret key.
              </li>
              <li>
                <strong>MoneyEU makes the final routing decision, so check what comes back.</strong>{' '}
                If <code>data.checkoutUrl</code> is a MoneyEU checkout link (its path starts with{' '}
                <code>/pay/</code>), pass it to <code>MoneyEU.mount</code>. Otherwise this payment is
                completed on a hosted page — redirect the customer to <code>data.paymentUrl</code> as
                usual. The embed script refuses any other link, so handling both keeps every payment
                working.
              </li>
              <li>
                Save <code>data.transactionId</code> against your own order. It is how you match the
                status webhook to the order later.
              </li>
              <li>
                <strong>Send the customer&apos;s real email address in <code>customerEmail</code>.</strong>{' '}
                For identity-verified payment methods the payment is tied to that address, and a
                customer who signs in with a different one cannot complete it.
              </li>
            </ul>

            <p className="apidoc-step-label">Page requirements</p>
            <ul className="apidoc-list">
              <li>
                <strong>Serve the page over HTTPS.</strong> Browsers refuse camera and payment access
                to insecure pages.
              </li>
              <li>
                <strong>Don&apos;t fix the container&apos;s height,</strong> and don&apos;t place it inside a
                small popup or an element with <code>overflow: hidden</code>. The script sizes the
                frame to the checkout, and the identity-document step needs about 760px of height.
              </li>
              <li>
                <strong>Width:</strong> about 720px or wider shows the order summary beside the payment
                form. Narrower containers still work — the layout stacks.
              </li>
              <li>
                <strong>Content Security Policy:</strong> if your site sends one, allow{' '}
                <code>script-src https://login.moneyeu.com</code> and{' '}
                <code>frame-src https://login.moneyeu.com</code>.
              </li>
            </ul>

            <p className="apidoc-step-label">What your customers will see</p>
            <p className="apidoc-p apidoc-hint">
              For identity-verified payment methods the checkout takes a few more steps than a card
              payment. Letting customers know before they start reduces drop-off:
            </p>
            <ul className="apidoc-list">
              <li>Sign in to a saved payment account with a one-time code sent by SMS.</li>
              <li>Enter identity details. For US customers, including a full Social Security number.</li>
              <li>Add a card.</li>
              <li>For some amounts, take a photo of a government ID and a selfie.</li>
              <li>See a &ldquo;processing&rdquo; state while the payment is confirmed.</li>
            </ul>

            <div className="apidoc-warning" role="note">
              <BsExclamationCircle className="apidoc-warning-icon" aria-hidden="true" />
              <div>
                <strong>Fulfil only on a server-confirmed result.</strong>{' '}
                <code>onResult</code>, <code>onPending</code> and every other callback are signals from
                the browser, which a customer can tamper with. Ship goods only once your{' '}
                <a href="#status-webhook">status webhook</a> or the{' '}
                <a href="#transaction-status">transaction status endpoint</a> reports{' '}
                <code>COMPLETED</code>. Some payment methods take a few minutes to confirm after the
                customer finishes.
              </div>
            </div>

            <p className="apidoc-step-label">Or embed the checkout URL manually</p>
            <p className="apidoc-p apidoc-hint">
              Append <code>parentOrigin</code> with your page&apos;s origin so postMessage is
              targeted at your site. If you omit it, checkout falls back to{' '}
              <code>ancestorOrigins</code> / the document referrer, then to <code>*</code>. Always
              pass <code>parentOrigin</code> in production.
            </p>
            <CodeBlock language="HTML" code={IFRAME_EMBED_HTML} />

            <p className="apidoc-step-label">Listen for the result</p>
            <p className="apidoc-p apidoc-hint">
              Reject any message whose <code>event.origin</code> is not the MoneyEU checkout origin,
              and ignore anything that is not <code>type: &quot;moneyeu.checkout.result&quot;</code>.
              Treat the message as a UI signal only confirm the outcome with your{' '}
              <a href="#status-webhook">callbackUrl status webhook</a> or the{' '}
              <a href="#transaction-status">transaction status endpoint</a>.
            </p>
            <CodeBlock language="JavaScript" code={IFRAME_LISTEN_JS} />

            <p className="apidoc-step-label">Message payload</p>
            <CodeBlock language="JSON" code={IFRAME_MESSAGE_SAMPLE} />

            <div className="apidoc-table-wrap apidoc-table-wrap-spaced">
              <table className="apidoc-table">
                <thead>
                  <tr><th>Field</th><th>Type</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>type</code></td><td>string</td><td>Always <code>moneyeu.checkout.result</code>.</td></tr>
                  <tr><td><code>status</code></td><td>string</td><td>Raw gateway status e.g. <code>COMPLETED</code>, <code>DECLINED</code>, <code>FAILED</code>. In-progress states such as <code>AWAITING_3DS</code> are not posted.</td></tr>
                  <tr>
                    <td><code>outcome</code></td>
                    <td>string</td>
                    <td>
                      Normalized result for your UI: <code>success</code> or <code>error</code>.
                      Messages are posted only for final outcomes in-progress states such as{' '}
                      <code>PENDING</code> / <code>AWAITING_3DS</code> are not sent.
                    </td>
                  </tr>
                  <tr><td><code>transactionId</code></td><td>string</td><td>Transaction identifier. Use this to poll status or match your webhook.</td></tr>
                  <tr><td><code>amount</code></td><td>number | null</td><td>Payment amount.</td></tr>
                  <tr><td><code>currency</code></td><td>string | null</td><td>3-letter ISO currency code.</td></tr>
                  <tr><td><code>message</code></td><td>string | null</td><td>Human-readable status summary.</td></tr>
                  <tr><td><code>reason</code></td><td>string | null</td><td>Additional detail acquirer message, decline reason, or sandbox note.</td></tr>
                  <tr><td><code>acquirerReference</code></td><td>string | null</td><td>Acquirer reference, when available.</td></tr>
                </tbody>
              </table>
            </div>

            <p className="apidoc-step-label">3DS and redirects</p>
            <ul className="apidoc-list">
              <li>
                If the acquirer requires 3DS, the challenge stays inside the iframe. After it
                completes, the status page in that same frame posts the same{' '}
                <code>moneyeu.checkout.result</code> message.
              </li>
              <li>
                Your <code>redirectUrl</code> / <code>merchantRedirectUrl</code> is skipped while
                checkout is framed, so the customer is not navigated away from your page. Close or
                replace the iframe yourself when you receive the result.
              </li>
              <li>
                If you embed the iframe manually, give it enough height (around 720px, more for
                identity-verified payment methods) so the form and any 3DS challenge are usable
                without clipping. The embed script handles this for you.
              </li>
            </ul>
          </section>

          <section id="transaction-status" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">9. GET Transaction Status</h4>
              <span className="cm-section-hint">Check transaction status</span>
            </div>
            <p className="apidoc-p">
              Get the transaction status of a payment using the <code>transactionId</code> returned from
              a successful <code>processPayment</code> call. The same URL is also returned as{' '}
              <code>data.redirectUrl</code> in the process-payment response useful for redirecting
              customers after 3DS or for server-side status checks.
            </p>
            <p className="apidoc-p apidoc-hint">
              No authentication headers or request signature are required for this endpoint.
            </p>

            <p className="apidoc-step-label">Request</p>
            <div className="apidoc-table-wrap">
              <table className="apidoc-table">
                <thead>
                  <tr><th>Method</th><th>Path</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>GET</code></td>
                    <td><code>/v1/payments/redirect/status/&#123;transactionId&#125;</code></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="apidoc-table-wrap apidoc-table-wrap-spaced">
              <table className="apidoc-table">
                <thead>
                  <tr><th>Path parameter</th><th>Required</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>transactionId</code></td>
                    <td>Yes</td>
                    <td>The transaction ID from the process-payment response (e.g. <code>TXN-83d216a3-a1b6-41bd-b6f7-e467fc604fa5</code>).</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="apidoc-step-label">Example request</p>
            <CodeBlock language="cURL" code={TRANSACTION_STATUS_CURL} />

            <p className="apidoc-step-label">Success response:  200 OK</p>
            <CodeBlock language="JSON" code={TRANSACTION_STATUS_RESPONSE} />

            <div className="apidoc-table-wrap apidoc-table-wrap-spaced">
              <table className="apidoc-table">
                <thead>
                  <tr><th>Field</th><th>Type</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>transactionId</code></td><td>string</td><td>Transaction identifier.</td></tr>
                  <tr><td><code>status</code></td><td>string</td><td>Current status e.g. <code>PENDING</code>, <code>AWAITING_3DS</code>, <code>COMPLETED</code>, <code>FAILED</code>, <code>DECLINED</code>.</td></tr>
                  <tr><td><code>amount</code></td><td>number</td><td>Payment amount.</td></tr>
                  <tr><td><code>currency</code></td><td>string</td><td>3-letter ISO currency code.</td></tr>
                  <tr><td><code>message</code></td><td>string</td><td>Human-readable status summary.</td></tr>
                  <tr><td><code>reason</code></td><td>string</td><td>Additional detail acquirer message, decline reason, or sandbox note.</td></tr>
                  <tr><td><code>merchantRedirectUrl</code></td><td>string</td><td>Your <code>redirectUrl</code> from the original payment request, if configured. Used to send the customer back to your site once the payment reaches a final state.</td></tr>
                  <tr><td><code>acquirerReference</code></td><td>string</td><td>Acquirer reference, when available.</td></tr>
                  <tr><td><code>redirectTimerSeconds</code></td><td>number | null</td><td>Auto-redirect countdown (seconds) before sending the customer to <code>merchantRedirectUrl</code>, when configured on your merchant account.</td></tr>
                </tbody>
              </table>
            </div>

            <p className="apidoc-step-label">Status values</p>
            <div className="apidoc-table-wrap">
              <table className="apidoc-table">
                <thead>
                  <tr><th>Status</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>PENDING</code></td><td>Payment submitted and not yet final.</td></tr>
                  <tr><td><code>AWAITING_3DS</code></td><td>Customer must complete 3DS / OTP authentication. Keep polling until the status changes.</td></tr>
                  <tr><td><code>COMPLETED</code></td><td>Payment succeeded.</td></tr>
                  <tr><td><code>DECLINED</code></td><td>Card was declined.</td></tr>
                  <tr><td><code>FAILED</code></td><td>Payment failed.</td></tr>
                </tbody>
              </table>
            </div>

            <p className="apidoc-p apidoc-hint">
              Poll while <code>status</code> is <code>PENDING</code> or <code>AWAITING_3DS</code>{' '}
              (customer completing 3DS). Once the status is final, rely on your{' '}
              <a href="#status-webhook">callbackUrl status webhook</a> for server-to-server
              confirmation and use <code>merchantRedirectUrl</code> to return the customer to your
              site.
            </p>
          </section>

          <section id="test-cards" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">10. Test card numbers</h4>
              <span className="cm-section-hint">Sandbox / test mode only</span>
            </div>
            <p className="apidoc-p">
              When your merchant account or terminal is in test mode, use the card numbers below to
              simulate different payment outcomes. Any valid future expiry date and any 3-digit CVV
              may be used unless noted otherwise.
            </p>
            <div className="apidoc-table-wrap">
              <table className="apidoc-table">
                <thead>
                  <tr>
                    <th>Card number</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>4242 4242 4242 4242</code></td>
                    <td>Always succeeds</td>
                  </tr>
                  <tr>
                    <td><code>4000 0000 0000 0002</code></td>
                    <td>Always declined</td>
                  </tr>
                  <tr>
                    <td><code>4000 0000 0000 9995</code></td>
                    <td>Declined insufficient funds</td>
                  </tr>
                  <tr>
                    <td><code>4000 0027 6000 3184</code></td>
                    <td>Requires OTP status becomes <code>AWAITING_3DS</code>; use code <code>123456</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="apidoc-p apidoc-hint">
              Submit card numbers without spaces in API requests (e.g. <code>4242424242424242</code>).
              These cards apply to both direct S2S payments and the hosted checkout pay step.
            </p>
          </section>

          <section id="errors" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">11. Error codes</h4>
            </div>

            <p className="apidoc-step-label">Authentication &amp; signature errors</p>
            <div className="apidoc-table-wrap">
              <table className="apidoc-table apidoc-table-compact">
                <thead>
                  <tr><th>Error code</th><th>HTTP status</th><th>Meaning</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>API_KEY_REQUIRED</code></td><td>401</td><td><code>apiKey</code> header missing.</td></tr>
                  <tr><td><code>MERCHANT_NOT_FOUND</code></td><td>401</td><td><code>apiKey</code> doesn't match a known merchant.</td></tr>
                  <tr><td><code>MERCHANT_SECRET_NOT_CONFIGURED</code></td><td>401</td><td>Merchant has no secret key configured.</td></tr>
                  <tr><td><code>SALT_REQUIRED</code></td><td>401</td><td><code>salt</code> header missing.</td></tr>
                  <tr><td><code>SALT_INVALID</code></td><td>401</td><td><code>salt</code> longer than 32 characters.</td></tr>
                  <tr><td><code>TIMESTAMP_REQUIRED</code></td><td>401</td><td><code>timestamp</code> header missing.</td></tr>
                  <tr><td><code>TIMESTAMP_INVALID</code></td><td>401</td><td><code>timestamp</code> not a valid Unix-epoch-seconds integer.</td></tr>
                  <tr><td><code>TIMESTAMP_EXPIRED</code></td><td>401</td><td><code>timestamp</code> more than 5 minutes from server time, either direction.</td></tr>
                  <tr><td><code>SIGNATURE_REQUIRED</code></td><td>401</td><td><code>signature</code> header missing.</td></tr>
                  <tr><td><code>INVALID_SIGNATURE</code></td><td>401</td><td>Computed signature doesn't match the one sent.</td></tr>
                  <tr><td><code>REQUEST_BODY_REQUIRED</code></td><td>400</td><td>Request body missing or blank.</td></tr>
                </tbody>
              </table>
            </div>

            <p className="apidoc-step-label">Payment &amp; checkout errors</p>
            <div className="apidoc-table-wrap">
              <table className="apidoc-table apidoc-table-compact">
                <thead>
                  <tr><th>Error code</th><th>HTTP status</th><th>Meaning</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>MERCHANT_TERMINAL_NOT_FOUND</code></td><td>400</td><td>Terminal not found for merchant, or terminal id invalid.</td></tr>
                  <tr><td><code>MERCHANT_TERMINAL_TRAFFIC_MISSING</code></td><td>400</td><td>Terminal has no traffic type configured.</td></tr>
                  <tr><td><code>ACQUIRER_NOT_MAPPED</code></td><td>400</td><td>No active acquirer mapping for the terminal.</td></tr>
                  <tr><td><code>ACQUIRER_ROUTING_NOT_FOUND</code></td><td>400</td><td>No acquirer terminal matches the requested flow/traffic type.</td></tr>
                  <tr><td><code>PAYMENT_ROUTING_FAILED</code></td><td>502</td><td>All acquirer attempts failed.</td></tr>
                  <tr><td><code>CHECKOUT_TOKEN_REQUIRED</code></td><td>400</td><td>Checkout token missing on a checkout-flow call.</td></tr>
                  <tr><td><code>CHECKOUT_NOT_FOUND</code></td><td>404</td><td>Checkout token does not match any order.</td></tr>
                  <tr><td><code>CHECKOUT_ALREADY_COMPLETED</code></td><td>410</td><td>Checkout link already used.</td></tr>
                  <tr><td><code>CHECKOUT_EXPIRED</code></td><td>410</td><td>Checkout link has expired.</td></tr>
                  <tr><td><code>CHECKOUT_ALREADY_IN_PROGRESS</code></td><td>409</td><td>Order is already being processed (concurrent submission).</td></tr>
                  <tr><td><code>TEST_PAYMENT_DECLINED</code></td><td>402</td><td>Simulated/test payment declined.</td></tr>
                </tbody>
              </table>
            </div>

            <p className="apidoc-step-label">Sample error response</p>
            <CodeBlock language="JSON" code={ERROR_SAMPLE} />
          </section>

          <section id="mistakes" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">12. Common mistakes</h4>
            </div>
            <ul className="apidoc-list">
              <li>
                <strong>Re-serializing the body before sending.</strong> Sign the exact string you
                send if you build the signature from one JSON string but a library
                re-serializes/reformats the body before it goes over the wire (different key order,
                extra whitespace, trailing newline), the signature won't match on the server.
              </li>
              <li>
                <strong>Hex instead of Base64.</strong> The signature is Base64-encoded. A
                hex-encoded HMAC of the same message will always fail <code>INVALID_SIGNATURE</code>.
              </li>
              <li>
                <strong>Wrong serviceName.</strong> It's the fixed string <code>moneyEuPayment</code>,
                not the endpoint path and not <code>processPayment</code>.
              </li>
              <li>
                <strong>Clock drift.</strong> <code>timestamp</code> must be within 5 minutes of the
                server's clock. Generate it right before sending the request, not ahead of time.
              </li>
              <li>
                <strong>Reusing a salt.</strong> Not currently checked for replay, but generate a
                fresh random salt per request as a matter of good practice.
              </li>
              <li>
                <strong>Trusting iframe postMessage as settlement.</strong> The{' '}
                <code>moneyeu.checkout.result</code> event is for updating your UI. Always confirm
                the outcome with your <a href="#status-webhook">callbackUrl status webhook</a> or
                the transaction status endpoint, and reject messages whose <code>event.origin</code>{' '}
                is not the MoneyEU checkout origin.
              </li>
              <li>
                <strong>Expecting <code>redirectUrl</code> inside an iframe.</strong> When checkout
                is framed, MoneyEU posts a message instead of navigating away. Handle the result
                in your parent page and close or replace the iframe yourself.
              </li>
            </ul>
          </section>

          <section id="notes" className="cm-card apidoc-section">
            <div className="cm-section-head">
              <h4 className="cm-section-title">13. Notes for integrators</h4>
            </div>
            <ul className="apidoc-list">
              <li>The <code>amount</code> field is a plain numeric value; do not include currency symbols.</li>
              <li><code>currency</code> and <code>countryName</code> should follow standard ISO codes/names where possible.</li>
              <li>
                Card data (<code>cardNumber</code>, <code>cvv</code>, <code>expiryDate</code>) is
                required only for direct server-to-server card flows and is masked immediately after
                use it is never returned in responses or stored in plain form.
              </li>
              <li>
                Status webhooks are dispatched asynchronously to <code>callbackUrl</code> after a
                payment succeeds or is declined; ensure this endpoint is publicly reachable and
                responds quickly with 2xx. See <a href="#status-webhook">Status webhook</a>.
              </li>
              <li>
                For iframe embeds, append <code>?parentOrigin=https://your-domain.com</code> to
                the checkout URL and listen for <code>type: &quot;moneyeu.checkout.result&quot;</code>.
                Pass your exact origin not a path.
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;
